import {
  BISHOP,
  BLACK,
  KING,
  KNIGHT,
  PAWN,
  QUEEN,
  ROOK,
  WHITE,
  countMaterial,
  materialValue,
  type ClockState,
  type Color,
  type PieceType,
  type Position,
} from '@chess/core'
import { memo, type JSX } from 'react'
import { PieceGlyph } from '../board/pieces.js'
import { Clock } from './Clock.js'

/** Starting counts, so what is missing can be shown as captured. */
const FULL_SET: Readonly<Record<PieceType, number>> = {
  [PAWN]: 8,
  [KNIGHT]: 2,
  [BISHOP]: 2,
  [ROOK]: 2,
  [QUEEN]: 1,
  [KING]: 1,
}

const ORDER: readonly PieceType[] = [PAWN, KNIGHT, BISHOP, ROOK, QUEEN]

/**
 * The pieces this player has captured, inferred from what the opponent is
 * missing. That is cheaper and more robust than tracking captures through the
 * move history, and it stays right when the board is scrubbed backwards.
 */
const capturedBy = (position: Position, color: Color): readonly PieceType[] => {
  const theirs = countMaterial(position, color === WHITE ? BLACK : WHITE)
  const remaining: Readonly<Record<PieceType, number>> = {
    [PAWN]: theirs.pawns,
    [KNIGHT]: theirs.knights,
    [BISHOP]: theirs.lightBishops + theirs.darkBishops,
    [ROOK]: theirs.rooks,
    [QUEEN]: theirs.queens,
    [KING]: 1,
  }
  return ORDER.flatMap((type) =>
    Array.from({ length: Math.max(0, FULL_SET[type] - remaining[type]) }, () => type),
  )
}

const advantageOf = (position: Position, color: Color): number => {
  const mine = materialValue(countMaterial(position, color))
  const theirs = materialValue(countMaterial(position, color === WHITE ? BLACK : WHITE))
  return mine - theirs
}

export interface PlayerBarProps {
  readonly name: string
  /** A signed-in player's picture; null for a guest, which is most people. */
  readonly avatar: string | null
  readonly color: Color
  readonly position: Position
  readonly clock: ClockState
  readonly turn: Color
  readonly live: boolean
  readonly offsetMs: number
  readonly online: boolean
  readonly showClock: boolean
  /** True while a bot is deciding, so it does not look frozen. */
  readonly thinking: boolean
  readonly onUrgent?: () => void
}

const PlayerBarImpl = ({
  name,
  avatar,
  color,
  position,
  clock,
  turn,
  live,
  offsetMs,
  online,
  showClock,
  thinking,
  onUrgent,
}: PlayerBarProps): JSX.Element => {
  const captured = capturedBy(position, color)
  const advantage = advantageOf(position, color)

  return (
    <div className="no-select flex items-center justify-between gap-2 px-1 py-1.5 sm:px-0">
      <div className="flex min-w-0 items-center gap-2">
        {/* The colour chip doubles as the avatar frame, so a signed-in player
            is recognisable without the row growing or reflowing. */}
        {avatar === null ? (
          <span
            className={`h-7 w-7 shrink-0 rounded-sm border ${
              color === WHITE
                ? 'border-neutral-400 bg-neutral-100'
                : 'border-neutral-900 bg-neutral-700'
            }`}
            aria-hidden="true"
          />
        ) : (
          <img
            src={avatar}
            alt=""
            referrerPolicy="no-referrer"
            className={`h-7 w-7 shrink-0 rounded-sm border object-cover ${
              color === WHITE ? 'border-neutral-400' : 'border-neutral-900'
            }`}
          />
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-ink">{name}</span>
            <span
              className={`h-1.5 w-1.5 shrink-0 rounded-full ${online ? 'bg-accent' : 'bg-neutral-500'}`}
              title={online ? 'Online' : 'Away'}
            />
            {thinking && (
              <span className="flex shrink-0 items-center gap-0.5" aria-label="Thinking">
                {[0, 1, 2].map((index) => (
                  <span
                    key={index}
                    className="thinking-dot h-1 w-1 rounded-full bg-ink-dim"
                    style={{ animationDelay: `${index * 160}ms` }}
                  />
                ))}
              </span>
            )}
          </div>
          <div className="flex h-4 items-center gap-px">
            {captured.map((type, index) => (
              <PieceGlyph
                key={`${type}-${index}`}
                color={color === WHITE ? BLACK : WHITE}
                type={type}
                className="-ml-1 h-4 w-4 first:ml-0"
              />
            ))}
            {advantage > 0 && <span className="ml-1 text-xs text-ink-dim">+{advantage}</span>}
          </div>
        </div>
      </div>

      {showClock && (
        <Clock
          clock={clock}
          turn={turn}
          color={color}
          live={live}
          offsetMs={offsetMs}
          {...(onUrgent === undefined ? {} : { onUrgent })}
        />
      )}
    </div>
  )
}

export const PlayerBar = memo(PlayerBarImpl)
